import React, { FC, Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './CheckoutDeliverySection.module.css';
import DropdownWithMedia from '../UILibrary/DropdownWithMedia';
import AddDeliveryAddressModal from '../Modals/AddDeliveryAddressModal';
import ConfirmModal from '../Modals/ConfirmModal';
import { useNavigate } from 'react-router-dom';
import { useForceLogoutAndRedirect } from 'Logic/Functions/Hooks/useForceLogoutAndRedirect';
import { UnauthorizedPopupStoreImplementation } from 'Data/UnauthorizedPopup/UnauthorizedPopupStoreImplementation';
import { UserAuthenticationStoreImplementation } from 'Data/UserAuthentication/UserAuthenticationStoreImplementation';
import { DeliveryAddressesStoreImplementation } from 'Data/DeliveryAddresses/DeliveryAddressesStoreImplementation';
import { DeliveryAddressService } from 'Logic/DeliveryAddressService';
import type { IDeliveryAddressView } from '@model/DeliveryAddress.model';
import TrashIcon from 'Utils/Assets/SVGs/TrashIcon';
import DeliveryCarrierSection from './CheckoutDeliveryCarrierSection';
import { OrderStoreImplementation } from 'Data/Order/OrderStoreImplementation';

const CheckoutDeliverySection: FC = () => {
  const navigate = useNavigate();
  const service = new DeliveryAddressService();
  const { authenticationState } = UserAuthenticationStoreImplementation();
  const authToken = authenticationState?.token?.trim() ?? '';
  const isLoggedIn = !!authToken;

  const onAuthError = useForceLogoutAndRedirect();
  const { showPopup } = UnauthorizedPopupStoreImplementation();
  const { deliveryAddressesState, loadAddresses, clearAddresses } =
    DeliveryAddressesStoreImplementation();
  const loading = deliveryAddressesState?.loading ?? false;
  const apiError = deliveryAddressesState?.apiError ?? null;
  const addresses: IDeliveryAddressView[] = deliveryAddressesState?.items ?? [];
  const mustAddAddressToProceed = isLoggedIn && !loading && addresses.length === 0;

  const { selectedDeliveryAddress, setSelectedDeliveryAddress } = OrderStoreImplementation();

  const [openAddModal, setOpenAddModal] = useState(false);
  const [settingDefault, setSettingDefault] = useState(false);
  const [setDefaultError, setSetDefaultError] = useState<string | null>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const resetSelection = useCallback(
    () => setSelectedDeliveryAddress(null),
    [setSelectedDeliveryAddress]
  );

  const handlersRef = useRef({
    loadAddresses,
    clearAddresses,
    resetSelection,
    onAuthError,
    onForbidden: (msg: string) => {
      showPopup(msg);
      navigate('/', { replace: true });
    },
  });

  useEffect(() => {
    handlersRef.current = {
      loadAddresses,
      clearAddresses,
      resetSelection,
      onAuthError,
      onForbidden: (msg: string) => {
        showPopup(msg);
        navigate('/', { replace: true });
      },
    };
  }, [loadAddresses, clearAddresses, resetSelection, onAuthError, showPopup, navigate]);

  useEffect(() => {
    service.handleAuthStateChange({
      token: authToken,
      clearAddresses: handlersRef.current.clearAddresses,
      resetSelection: handlersRef.current.resetSelection,
      loadAddresses: handlersRef.current.loadAddresses,
      onAuthError: handlersRef.current.onAuthError,
      onForbidden: handlersRef.current.onForbidden,
      setOpenAddModal,
      setOpenDeleteModal,
      setDeleteError,
    });
  }, [authToken]);

  useEffect(() => {
    service.computeSelectedAddress({
      addresses,
      selectedAddress: selectedDeliveryAddress,
      setSelectedAddress: setSelectedDeliveryAddress,
    });
  }, [addresses, selectedDeliveryAddress, setSelectedDeliveryAddress]);

  const options = useMemo(() => service.buildCheckoutOptions(addresses), [addresses, service]);
  const dropdownValue = selectedDeliveryAddress?.id ?? '';

  const refreshAddresses = useCallback(async () => {
    await service.refreshAddresses({
      token: authToken,
      loadAddresses,
      onAuthError,
      onForbidden: (msg) => {
        showPopup(msg);
        navigate('/', { replace: true });
      },
      resetSelection: () => setSelectedDeliveryAddress(null),
    });
  }, [authToken, loadAddresses, onAuthError, showPopup, navigate, setSelectedDeliveryAddress]);

  const onOpenAdd = useCallback(() => {
    service.handleOpenAdd({ isLoggedIn, setOpenAddModal });
  }, [isLoggedIn]);

  const onCloseAdd = useCallback(() => {
    service.handleCloseAdd({ setOpenAddModal });
  }, []);

  const onCreated = useCallback(async () => {
    await service.handleCreated({ refreshAddresses, setOpenAddModal });
  }, [refreshAddresses]);

  const showSetDefault = !!selectedDeliveryAddress && !selectedDeliveryAddress.isDefaultAddress;

  const setAsDefault = useCallback(async () => {
    await service.handleSetDefaultFlow({
      authToken,
      selectedAddress: selectedDeliveryAddress,
      setLoading: setSettingDefault,
      setApiError: setSetDefaultError,
      clearError: () => setSetDefaultError(null),
      onAuthError,
      onForbidden: (msg) => {
        showPopup(msg);
        navigate('/', { replace: true });
      },
      refreshAddresses,
    });
  }, [authToken, selectedDeliveryAddress, onAuthError, refreshAddresses]);

  const confirmDeleteSelected = useCallback(async () => {
    await service.handleDeleteDeliveryAddressAndRefresh({
      addressId: selectedDeliveryAddress?.id ?? '',
      selectedAddress: selectedDeliveryAddress,
      token: authToken,
      setLoading: setDeleting,
      setApiError: setDeleteError,
      onAuthError,
      onForbidden: (msg) => {
        showPopup(msg);
        navigate('/', { replace: true });
      },
      refreshAddresses,
      clearSelectedId: () => setSelectedDeliveryAddress(null),
    });
  }, [
    authToken,
    selectedDeliveryAddress,
    onAuthError,
    refreshAddresses,
    setSelectedDeliveryAddress,
  ]);

  const openDeleteForSelected = useCallback(() => {
    service.handleOpenDeleteDeliveryAddress({
      selectedAddress: selectedDeliveryAddress,
      clearError: () => setDeleteError(null),
      openModal: () => setOpenDeleteModal(true),
    });
  }, [selectedDeliveryAddress]);

  const handleSelectAddress = useCallback(
    (id: string) => {
      service.selectAddress({
        id,
        addresses,
        setSelectedAddress: setSelectedDeliveryAddress,
      });
    },
    [addresses, setSelectedDeliveryAddress]
  );

  return (
    <section className={styles.section} data-testid="checkout-delivery-section">
      <header className={styles.header}>
        <div className={styles.titleWrap}>
          <div className={styles.titleIcon} aria-hidden="true" />
          <div>
            <h3 className={styles.title}>Shipping</h3>
            <p className={styles.subtitle}>Choose your delivery address</p>
          </div>
        </div>
        {addresses.length > 0 && (
          <button
            type="button"
            className={styles.addButtonPrimary}
            onClick={onOpenAdd}
            data-testid="checkout-add-address-button"
            disabled={!isLoggedIn}
          >
            Add new address
          </button>
        )}
      </header>
      {mustAddAddressToProceed && (
        <div className={styles.checkoutBlockedMsg} data-testid="checkout-must-add-address">
          Add a delivery address to proceed the order placement.
        </div>
      )}

      {apiError && (
        <div className={styles.error} data-testid="checkout-delivery-error">
          {apiError}
        </div>
      )}

      {setDefaultError && (
        <div className={styles.error} data-testid="checkout-set-default-error">
          {setDefaultError}
        </div>
      )}

      {deleteError && (
        <div className={styles.error} data-testid="checkout-delete-error">
          {deleteError}
        </div>
      )}

      <div className={styles.body}>
        {loading && (
          <div className={styles.placeholder} data-testid="checkout-delivery-loading">
            Loading addresses...
          </div>
        )}

        {!loading && addresses.length === 0 && (
          <div className={styles.empty} data-testid="checkout-no-addresses">
            <p className={styles.emptyText}>No delivery addresses yet.</p>
            <button
              type="button"
              className={styles.addButtonPrimary}
              onClick={onOpenAdd}
              disabled={!isLoggedIn}
              data-testid="checkout-add-first-address"
            >
              Add your first address
            </button>
          </div>
        )}

        {!loading && addresses.length > 0 && (
          <Fragment>
            <div data-testid="checkout-address-dropdown">
              <DropdownWithMedia
                value={dropdownValue}
                onChange={(v) => handleSelectAddress(String(v ?? ''))}
                placeholder="Select an address…"
                options={options}
                showThumbnail={false}
              />
            </div>

            {selectedDeliveryAddress && (
              <div className={styles.card} data-testid="checkout-selected-address">
                <div className={styles.cardRail} aria-hidden="true" />
                <div className={styles.cardMain}>
                  <div className={styles.cardTopRow}>
                    <span className={styles.cardLabel}>{selectedDeliveryAddress.addressLabel}</span>

                    {settingDefault ? (
                      <span className={styles.badge} data-testid="checkout-default-saving">
                        Saving...
                      </span>
                    ) : selectedDeliveryAddress.isDefaultAddress ? (
                      <span className={styles.badge} data-testid="checkout-default-badge">
                        Default
                      </span>
                    ) : (
                      <div className={styles.cardActions}>
                        {showSetDefault && (
                          <button
                            type="button"
                            className={styles.setDefaultBtn}
                            onClick={setAsDefault}
                            disabled={!isLoggedIn || settingDefault}
                            data-testid="checkout-set-default-button"
                          >
                            Set as default
                          </button>
                        )}

                        <button
                          type="button"
                          className={styles.deleteBtn}
                          onClick={openDeleteForSelected}
                          disabled={!isLoggedIn || deleting}
                          data-testid="checkout-delete-address-button"
                        >
                          <TrashIcon className={styles.uplIcon} width={18} height={18} />
                        </button>
                      </div>
                    )}
                  </div>

                  <div className={styles.cardLine}>
                    <span className={styles.bullet} aria-hidden="true" />
                    <span>
                      {selectedDeliveryAddress.street}, {selectedDeliveryAddress.houseNumber}
                    </span>
                  </div>

                  <div className={styles.cardLine}>
                    <span className={styles.bullet} aria-hidden="true" />
                    <span>
                      {selectedDeliveryAddress.city}, {selectedDeliveryAddress.region},{' '}
                      {selectedDeliveryAddress.country}
                    </span>
                  </div>

                  {!!selectedDeliveryAddress.postalCode && (
                    <div className={styles.cardLine}>
                      <span className={styles.bullet} aria-hidden="true" />
                      <span>Postal Code: {selectedDeliveryAddress.postalCode}</span>
                    </div>
                  )}

                  {!!selectedDeliveryAddress.additionalInformation && (
                    <div className={styles.cardLine}>
                      <span className={styles.bullet} aria-hidden="true" />
                      <span>Note: {selectedDeliveryAddress.additionalInformation}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div data-testid="checkout-carrier-section">
              <DeliveryCarrierSection />
            </div>
          </Fragment>
        )}
      </div>

      <AddDeliveryAddressModal
        open={openAddModal}
        token={authToken}
        onClose={onCloseAdd}
        onCreated={onCreated}
      />

      <ConfirmModal
        open={openDeleteModal}
        setOpen={setOpenDeleteModal}
        message="Are you sure you want to delete this delivery address? This action cannot be undone."
        confirmLabel="Delete address"
        cancelLabel="Cancel"
        onConfirm={confirmDeleteSelected}
      />
    </section>
  );
};

export default CheckoutDeliverySection;
